import styled from '@emotion/styled';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { DeprecatedCloseIcon } from '../../assets/icons';
import { ArnoldLoading } from '../../assets/illustrations';
import { theme } from '../../theme';
import { CleanButton } from '../buttons';

interface SideModalProps {
  // if the modal should be opened or not
  opened: boolean;
  // content of the modal
  children: React.ReactNode;
  // sticky header content rendered above the scrollable body
  header?: React.ReactNode;
  // primary button on the bottom right if provided
  primaryButton?: { label: string; onClick?: () => void; disabled?: boolean };
  // secondary button on the bottom right if provided
  secondaryButton?: { label: string; onClick?: () => void; disabled?: boolean };
  // close action is called when x buttom on top right is clicked or outside of the modal is clicked
  onCloseAction: () => void;
  // called when the modal is closed and the animation is completed
  onClosedAnimationComplete?: () => void;
  // for showing loading overlay
  loadingOverlay?: boolean;
  wide?: boolean; // if true, modal will be wider
  backgroundColor?: string;
  // if true, the modal will be displayed with a higher z-index
  // used for admin where z-indexes are different
  adminZIndex?: boolean;
}

const SLIDE_ANIMATION_DURATION = 300;

/**
 * Modal that slides in from the right side of the screen
 */
export const SideModal = ({
  opened,
  onCloseAction,
  onClosedAnimationComplete,
  children,
  header,
  primaryButton,
  secondaryButton,
  loadingOverlay,
  wide,
  backgroundColor,
  adminZIndex,
}: SideModalProps) => {
  const sideModalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenedRef = useRef(opened);
  const [hasOpened, setHasOpened] = useState(opened);
  // Initially closed drawers have no exit to animate. Keep their content mounted,
  // but enable both animations on the first open (and retain them for later closes).
  const shouldAnimate = opened || hasOpened;
  const onClosedAnimationCompleteRef = useRef(onClosedAnimationComplete);
  onClosedAnimationCompleteRef.current = onClosedAnimationComplete;

  useEffect(() => {
    const wasOpened = wasOpenedRef.current;
    wasOpenedRef.current = opened;

    let focusRafId: number | undefined;
    let closeTimeoutId: ReturnType<typeof setTimeout> | undefined;

    if (opened) {
      setHasOpened(true);
      previousFocusRef.current = document.activeElement as HTMLElement;
      focusRafId = requestAnimationFrame(() => {
        sideModalRef.current?.focus();
      });
    } else if (wasOpened) {
      closeTimeoutId = setTimeout(() => {
        onClosedAnimationCompleteRef.current?.();
      }, SLIDE_ANIMATION_DURATION);
      previousFocusRef.current?.focus();
    }

    return () => {
      if (focusRafId !== undefined) {
        cancelAnimationFrame(focusRafId);
      }
      if (closeTimeoutId !== undefined) {
        clearTimeout(closeTimeoutId);
      }
    };
  }, [opened]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!opened) return;

      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseAction?.();
        return;
      }

      if (e.key !== 'Tab') return;

      const modal = sideModalRef.current;
      if (!modal) return;

      const focusableElements = modal.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusableElements.length === 0) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [opened, onCloseAction],
  );

  const onOverlayClicked = () => {
    if (opened) {
      onCloseAction?.();
    }
  };

  return (
    <>
      {createPortal(
        <>
          <Overlay opened={opened} shouldAnimate={shouldAnimate} onClick={onOverlayClicked} adminZIndex={adminZIndex} />
          <StyledSideModal
            ref={sideModalRef}
            opened={opened}
            shouldAnimate={shouldAnimate}
            data-cy="side-modal"
            wide={wide}
            backgroundColor={backgroundColor}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            adminZIndex={adminZIndex}
          >
            {loadingOverlay && (
              <div className="loadingOverlay">
                <Loading />
              </div>
            )}
            {header ? (
              <>
                <div className="side-modal-header">
                  <CleanButton onClick={() => onCloseAction?.()} aria-label="Close modal">
                    <DeprecatedCloseIcon />
                  </CleanButton>
                  {header}
                </div>
                <div className="side-modal-body side-modal-body--with-header">{children}</div>
              </>
            ) : (
              <div className="side-modal-body">
                <StyledCleanButton onClick={() => onCloseAction?.()} aria-label="Close modal">
                  <DeprecatedCloseIcon />
                </StyledCleanButton>
                {children}
              </div>
            )}
            {(primaryButton || secondaryButton) && (
              <div className="side-modal-footer">
                {primaryButton && (
                  <Button onClick={primaryButton.onClick} disabled={primaryButton.disabled} data-cy="primary-button">
                    {primaryButton.label}
                  </Button>
                )}
                {secondaryButton && (
                  <Button
                    variant="outline-primary"
                    onClick={secondaryButton.onClick}
                    disabled={secondaryButton.disabled}
                    data-cy="secondary-button"
                  >
                    {secondaryButton.label}
                  </Button>
                )}
              </div>
            )}
          </StyledSideModal>
        </>,
        document.body,
      )}
    </>
  );
};

const Overlay = styled.div<{ opened?: boolean; shouldAnimate: boolean; adminZIndex?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: ${({ adminZIndex }) => (adminZIndex ? 2000 : 214748369)};
  background: ${(props) => (props.opened ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0)')};
  animation: ${({ opened, shouldAnimate }) =>
    shouldAnimate ? `${opened ? 'fadeIn' : 'fadeOut'} ${SLIDE_ANIMATION_DURATION}ms linear` : 'none'};
  pointer-events: ${(props) => (props.opened ? 'auto' : 'none')};

  @keyframes fadeIn {
    from {
      background: rgba(0, 0, 0, 0);
    }
    to {
      background: rgba(0, 0, 0, 0.3);
    }
  }

  @keyframes fadeOut {
    from {
      background: rgba(0, 0, 0, 0.3);
    }
    to {
      background: rgba(0, 0, 0, 0);
    }
  }
`;

const StyledSideModal = styled.div<{
  opened?: boolean;
  shouldAnimate: boolean;
  wide?: boolean;
  backgroundColor?: string;
  adminZIndex?: boolean;
}>`
  position: fixed;
  top: 0;
  right: 0;
  outline: none;
  width: ${({ wide }) => (wide ? 'min(680px, 100%)' : 'min(640px, 100%)')};
  max-width: 90%;
  height: 100%;
  background: ${({ backgroundColor }) => backgroundColor || theme.colors.backgroundBasic.default};
  z-index: ${({ adminZIndex }) => (adminZIndex ? 2001 : 214748369)};
  display: flex;
  flex-direction: column;
  box-shadow: ${({ opened }) => (opened ? '0px 0px 20px 0px #00000080' : 'none')};

  transform: translateX(${(props) => (props.opened ? '0' : '100%')});
  animation: ${({ opened, shouldAnimate }) =>
    !shouldAnimate
      ? 'none'
      : opened
        ? `slideIn ${SLIDE_ANIMATION_DURATION}ms cubic-bezier(0, 0, 0.2, 1)`
        : `slideOut ${SLIDE_ANIMATION_DURATION}ms cubic-bezier(0.8, 0, 1, 1)`};
  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @keyframes slideOut {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(100%);
    }
  }

  .side-modal-header {
    flex-shrink: 0;
    padding: ${theme.spacing.f} ${theme.spacing.i};
    border-bottom: 1px solid ${theme.colors.borderSeparator.default};

    .close-button {
      margin-top: 0;
      margin-bottom: ${theme.spacing.f};
    }
  }

  .side-modal-body {
    flex-grow: 1;
    overflow-y: auto;
    padding: ${theme.spacing.i};
    padding-top: ${theme.spacing.f};
  }

  .side-modal-body--with-header {
    padding-top: 0;
  }

  .side-modal-footer {
    display: flex;
    justify-content: flex-start;
    padding: ${theme.spacing.f} ${theme.spacing.i};
    background-color: white;
    box-shadow: 0px 0px 16px 0px #00000014;
    z-index: 5; // for box-shadow over siblings

    button {
      margin: 0px !important;
      margin-right: ${theme.spacing.f} !important;
    }
  }

  .close-button {
    margin-top: ${theme.spacing.g};
    margin-bottom: ${theme.spacing.i};
    cursor: pointer;
    background-color: white;
    width: 30px;
    height: 30px;
    text-align: center;
    align-content: center;
    border-radius: ${theme.borderRadius.s};
  }

  .loadingOverlay {
    position: absolute;
    z-index: 2002;
    width: 100%;
    height: 100%;
  }
`;

const Loading = () => (
  <Container>
    <CenterAnimation>
      <ArnoldLoading />
    </CenterAnimation>
  </Container>
);

const Container = styled.div(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 2005,
  width: '100%',
  height: '100%',
  outline: '0',
  transition: 'opacity 0.15s linear',
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
}));

const CenterAnimation = styled.div(() => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 128,
}));

const StyledCleanButton = styled(CleanButton)(() => ({
  marginTop: theme.spacing.g,
  marginBottom: theme.spacing.i,
}));

export default Loading;
